import {
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import CircleIcon from '@mui/icons-material/Circle';
// eslint-disable-next-line no-use-before-define
import React, { FC, useEffect, useState } from 'react';
import Router, { useRouter } from 'next/router';
import { SearchApi as OrganizationsSearchApi } from '@api/organizations/search';
import { SearchApi as PeopleSearchApi } from '@api/emails//search';
import { SearchApi as AwardsSearchApi } from '@api/awards//search';
import { SearchApi as PublicationsSearchApi } from '@api/publications///search';
import { CriteriaData, Field } from '@models/CriteriaData';
import { getImpactIconName } from '../../../elements/ImpactIndicator/functions';
import { Loading } from '../../../elements/Loading';
import { rewriteRouteWithNewCriteria } from '../../../../shared/functions';

interface CustomTooltipProps {
  title: string;
  icon?: string;
  data: {
    id: string;
    label: string;
    value: number;
    color?: string;
    total?: number;
  };
  filterQuery?: Field;
  api: 'affiliations' | 'people' | 'awards' | 'publications';
}

export const CustomTooltip: FC<CustomTooltipProps> = ({
  title,
  data,
  icon = 'material-symbols:data-object',
  filterQuery,
  api,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [entities, setEntities] = useState<
    { name: string; docCount?: number }[]
  >([]);

  const getFullTitle = (title: string, entitiesLength: number | undefined) => {
    if (entitiesLength && entitiesLength > 0) {
      return `Top ${entities?.length} ${title}`;
    }

    return title;
  };

  const router = useRouter();

  const criteria = JSON.parse(
    router.query.criteria ? (router.query.criteria as string) : '[]',
  ) as CriteriaData[];

  useEffect(() => {
    function extractImpactAffiliations(impact) {
      return impact
        .flatMap((s) => s.impact?.filter((impact) => impact.level === data.id))
        .filter(
          (value, index, self) =>
            index ===
            self.findIndex((t) => t.affiliations === value.affiliations),
        )
        .map((impact) => ({ name: impact.affiliations }));
    }

    setLoading(true);
    // eslint-disable-next-line default-case
    switch (api) {
      case 'affiliations':
        OrganizationsSearchApi.generalSearch(criteria, 0, {
          mostRecentPublications: router.query.mostRecentPublication,
          totalPublications:
            router.query.totalPublications === '-1'
              ? null
              : router.query.totalPublications,
          risk: router.query.risk,
          impact: data.id,
        }).then((res) => {
          setEntities(extractImpactAffiliations(res.data.summary));

          setLoading(false);
        });
        break;
      case 'people':
        PeopleSearchApi.fetchEmails(criteria, 0, {
          totalPublications:
            router.query.totalPublications === '-1'
              ? null
              : router.query.totalPublications,
          year: router.query.year,
          risk: router.query.risk,
          impact: data.id,
        }).then((res) => {
          setEntities(extractImpactAffiliations(res.data.emails));
          setLoading(false);
        });
        break;
      case 'awards':
        AwardsSearchApi.fetchFunderAwards(criteria, 0, {
          totalPublications:
            router.query.totalPublications === '-1'
              ? null
              : router.query.totalPublications,
          year: router.query.year,
          risk: router.query.risk,
          impact: data.id,
        }).then((res) => {
          setEntities(extractImpactAffiliations(res.data.awards));
          setLoading(false);
        });
        break;
      case 'publications':
        PublicationsSearchApi.generalSearch(criteria, 0, {
          totalPublications:
            router.query.totalPublications === '-1'
              ? null
              : router.query.totalPublications,
          year: router.query.year,
          risk: router.query.risk,
          impact: data.id,
        }).then((res) => {
          setEntities(extractImpactAffiliations(res.data.stData));
          setLoading(false);
        });
        break;
    }
  }, [data]);

  return (
    <>
      <Stack
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        sx={{
          backgroundColor: 'rgba(0,0,0,0.5)',
          py: 1,
          px: 1,
          mb: 0.5,
        }}
      >
        <Typography>{getFullTitle(title, entities?.length)}</Typography>
        <Icon icon={icon} fontSize={25} />
      </Stack>
      <Stack spacing={1} direction={'row'} alignItems={'center'}>
        <Icon icon={getImpactIconName(data?.id, 'square')} fontSize={15} />
        <Stack direction={'row'} spacing={1}>
          <Typography variant="body2">{`${data?.label}`}</Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'bolder',
            }}
          >{`(${Math.floor((data?.value / data.total) * 100)}%)`}</Typography>
        </Stack>
      </Stack>
      <List sx={{ minHeight: 50, maxHeight: 350, overflow: 'auto' }}>
        {loading && (
          <Stack
            alignItems={'center'}
            justifyContent={'center'}
            sx={{ py: 2, mt: 5 }}
          >
            <Loading sx={{ color: 'white' }} />
          </Stack>
        )}
        {entities?.map((entity) => (
          <ListItemButton
            key={entity.name}
            onClick={() => {
              if (filterQuery) {
                rewriteRouteWithNewCriteria(Router, {
                  q: entity.name,
                  field: filterQuery,
                  operator: 'AND',
                  accuracy: 'Vague',
                });
              }
            }}
          >
            <CircleIcon style={{ fontSize: 10, marginRight: 8 }} />
            <ListItemText>{entity.name}</ListItemText>
          </ListItemButton>
        ))}
      </List>
    </>
  );
};
